import React from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';

import api from '../services/api';
import LoadingPage from './LoadingPage';

let AuthContext = React.createContext(null);

const TOKEN_KEY = 'token';

// Normaliza a lista de permissões para um array de strings,
// aceitando tanto { description } (contrato standalone) quanto
// { permission } (contrato anubis) ou strings puras.
function normalizePerms(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((p) => {
    if (typeof p === 'string') return p;
    return p?.permission ?? p?.description ?? '';
  }).filter(Boolean);
}

export function AuthProvider({ children }) {
  const [user, setUser] = React.useState();
  const [perms, setPerm] = React.useState([]);
  const [name, setName] = React.useState();
  const [loading, setLoading] = React.useState(true);
  const navigate = useNavigate();

  React.useEffect(() => {
    const initAuth = async () => {
      setLoading(true);
      if (validateToken()) {
        try {
          await getUser();
        } catch (error) {
          setUser(null);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  function getToken() {
    return document.cookie
      .split(';')
      .map((row) => row.trim())
      .find((row) => row.startsWith(`${TOKEN_KEY}=`))
      ?.split('=')[1];
  }

  function setToken(tk) {
    const now = new Date();
    now.setTime(now.getTime() + 1000 * 60 * 30 * 48);
    document.cookie = `${TOKEN_KEY}=${tk}; expires=${now.toUTCString()}; path=/; SameSite=Strict`;
    api.defaults.headers.common['Authorization'] = tk;
  }

  function deleteCookies() {
    document.cookie = `${TOKEN_KEY}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Strict`;
    delete api.defaults.headers.common['Authorization'];
  }

  function validateToken() {
    const token = getToken();
    if (!token) return false;
    setToken(token);
    return true;
  }

  async function getUser() {
    try {
      const res = await api.get('/login/me/');
      setUser(res.data.username);
      setName(res.data.name);
      setPerm(normalizePerms(res.data.permissions));
      return res.data;
    } catch (err) {
      deleteCookies();
      setUser(null);
      setName(null);
      setPerm([]);
      throw err;
    }
  }

  const hasPerm = (p) => perms.some((perm) => perm === p);

  const refreshPerms = (useLoading = false) => {
    if (useLoading) setLoading(true);
    getUser().finally(() => setLoading(false));
  };

  const signin = async (cred, callback, clerr) => {
    try {
      const res = await api.post('/login/token/', cred);
      const token = `Bearer ${res.data.access_token}`;
      setToken(token);
      await getUser();
      if (callback) callback();
      return true;
    } catch (err) {
      if (clerr) clerr();
      return false;
    }
  };

  const signout = (callback = () => {}) => {
    // revoga o token no backend (best-effort) antes de limpar localmente
    api.post('/login/logout/').catch(() => {});
    setUser(null);
    deleteCookies();
    navigate('/login');
    if (callback) callback();
  };

  const value = {
    user,
    name,
    perms,
    loading,
    validateToken,
    signin,
    signout,
    hasPerm,
    getToken,
    refreshPerms,
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? <LoadingPage loading={loading} /> : children}
    </AuthContext.Provider>
  );
}

export function RequireAuth({ children }) {
  const auth = useAuth();
  const location = useLocation();

  if (auth?.loading) {
    return <LoadingPage loading={true} />;
  }
  if (!auth?.user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return children;
}

export function useAuth() {
  return React.useContext(AuthContext);
}
